/**
 * El panel — ADR-024.
 *
 * Vive DENTRO de la PWA y no en una app aparte: con un usuario en
 * producción, una app separada sería otro build, otro despliegue y otra
 * sesión que mantener, para nadie.
 *
 * UN 404 AQUÍ NO ES UN ERROR: es la respuesta de la API a quien no es admin,
 * y por diseño es indistinguible de una ruta inexistente (el 403 confirmaría
 * que hay algo detrás). Esta pantalla lo trata como "esto no es para ti", sin
 * decir que existe un panel.
 *
 * La cola muestra el `place_pitch` COMPLETO y nunca truncado. Es el único
 * dato con el que se decide, y cortarlo a tres palabras convertiría la
 * decisión en una lotería.
 */

import { useCallback, useEffect, useState } from 'react';

import { Boton, Pantalla, Stack, Texto, Titulo } from '../../shared/ui/primitives.js';
import * as api from '../../shared/lib/api.js';

type Carga = 'cargando' | 'lista' | 'no_es_para_ti' | 'falló';

export function AdminPanel({ onVolver }: { onVolver?: () => void }) {
  const [carga, setCarga] = useState<Carga>('cargando');
  const [solicitudes, setSolicitudes] = useState<api.AdminRequest[]>([]);
  const [ajustes, setAjustes] = useState<api.AdminSettings | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const cargar = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([api.fetchAdminRequests(), api.fetchAdminSettings()]);
      setSolicitudes(s);
      setAjustes(a);
      setCarga('lista');
    } catch (e) {
      // 404 = no eres admin. No se dice que el panel existe.
      setCarga(e instanceof api.ApiError && e.status === 404 ? 'no_es_para_ti' : 'falló');
    }
  }, []);

  useEffect(() => {
    void cargar();
  }, [cargar]);

  async function aprobar(id: string, nombre: string) {
    try {
      const r = await api.approveRequest(id);
      setAviso(`${nombre} está dentro. Su pase: ${r.inviteCode}`);
      await cargar();
    } catch {
      setAviso('No se pudo aprobar. Vuelve a intentar.');
    }
  }

  async function archivar(id: string) {
    try {
      await api.archiveRequest(id);
      await cargar();
    } catch {
      setAviso('No se pudo archivar.');
    }
  }

  async function cambiarModo(modo: string) {
    try {
      setAjustes(await api.writeAdminSetting('signup_mode', modo));
    } catch {
      setAviso('No se pudo cambiar.');
    }
  }

  if (carga === 'cargando') {
    return (
      <Pantalla centrada>
        <Texto variante="secundario" centrado>
          Un momento…
        </Texto>
      </Pantalla>
    );
  }

  if (carga === 'no_es_para_ti' || carga === 'falló') {
    return (
      <Pantalla centrada>
        <Stack>
          <Titulo centrado>Por aquí no hay nada</Titulo>
          {onVolver && <Boton onClick={onVolver}>Volver</Boton>}
        </Stack>
      </Pantalla>
    );
  }

  return (
    <Pantalla>
      <Stack>
        <header>
          <Titulo>Solicitudes</Titulo>
          <Texto variante="secundario">
            {solicitudes.length === 0
              ? 'Nadie esperando.'
              : `${solicitudes.length} ${solicitudes.length === 1 ? 'persona' : 'personas'} esperando.`}
          </Texto>
        </header>

        {aviso && <Texto>{aviso}</Texto>}

        {solicitudes.map((s) => (
          <div
            key={s.id}
            style={{
              background: 'var(--gu-surface-raised)',
              borderRadius: 'var(--gu-radius-lg)',
              padding: 'var(--gu-space-4)',
              display: 'grid',
              gap: 'var(--gu-space-3)',
            }}
          >
            <div>
              <Texto>
                <strong>{s.displayName}</strong>
              </Texto>
              <Texto variante="secundario">{s.email}</Texto>
            </div>

            <div>
              <Texto>
                <strong>{s.placePitch}</strong>
              </Texto>
              {/* Completo, nunca truncado: es el único dato con el que se decide. */}
              <Texto variante="secundario">{s.placePitchWhy}</Texto>
            </div>

            <Stack gap="var(--gu-space-2)">
              <Boton onClick={() => void aprobar(s.id, s.displayName)}>Que entre</Boton>
              <Boton variante="secundario" onClick={() => void archivar(s.id)}>
                Ahora no
              </Boton>
            </Stack>
          </div>
        ))}

        {ajustes && (
          <div
            style={{
              background: 'var(--gu-surface-sunken)',
              borderRadius: 'var(--gu-radius-lg)',
              padding: 'var(--gu-space-4)',
              display: 'grid',
              gap: 'var(--gu-space-3)',
            }}
          >
            <Titulo nivel={2}>La puerta</Titulo>
            <Texto variante="secundario">
              Pases al registrarse: {ajustes.invite_quota_default} · al primer lugar: +
              {ajustes.invite_quota_first_place}
            </Texto>

            <Stack gap="var(--gu-space-2)">
              {(['invite_only', 'request_open', 'closed'] as const).map((m) => (
                <Boton
                  key={m}
                  variante={ajustes.signup_mode === m ? 'primario' : 'secundario'}
                  onClick={() => void cambiarModo(m)}
                >
                  {m === 'invite_only'
                    ? 'Solo con pase'
                    : m === 'request_open'
                      ? 'Pase o solicitud'
                      : 'Cerrado'}
                </Boton>
              ))}
            </Stack>
          </div>
        )}

        {onVolver && (
          <Boton variante="secundario" onClick={onVolver}>
            Volver
          </Boton>
        )}
      </Stack>
    </Pantalla>
  );
}
